import React, {useEffect, useState} from 'react';
import {generateMicrolessonsText} from '../generateMicrolessonsText';
import PageTitle from '../../../../uiToolkit/PageTitle';
import './styles.css';
import BackButton from '../../../../uiToolkit/Buttons/BackButton';
import TextAreaInput from '../../../../uiToolkit/Inputs/TextAreaInput';
import ToggleSwitch from '../../../../uiToolkit/Inputs/ToggleSwitch';
import {
  ACK_NODE_OPTIONS,
  ASSESMENT_OPTIONS,
  BRANCHING_OPTIONS,
  SOURCE_TYPE,
} from '../conf';
import NumbersInput from '../../../../uiToolkit/Inputs/NumbersInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import {LessonGenerationFromPromptModel} from '../../../../data/models/AI/LessonGenerationModel';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import LibraryFilesInput from '../../../../uiToolkit/Inputs/LibraryFilesInput';
import Dropdown from '../../../../uiToolkit/Inputs/Dropdown';
import TextInput from '../../../../uiToolkit/Inputs/TextInput';
import {generateAIVideosText} from '../../GenerateAIVideo/generateAIVideosText';
import {numberWithCommas} from '../../../../utils/getFirstCapitals';
import {Tour} from '../../../../modules/Tour';
import ErrorBox2 from '../../../../uiToolkit/ErrorBox2/ErrorBox2';
import {internalErrorCodes} from '../../../../data/errorCodes';
import {
  DialectKey,
  Gender,
  LANGUAGE_CONFIG,
  LANGUAGE_OPTIONS,
  LanguageKey,
} from '../../../../data/models/AI/VideoGenerationOptions';
import {SelectOption} from '../../../../data/models/UI/SelectOption';
import YesNoOption from '../../../../uiToolkit/Inputs/YesNoOption';

interface Props {
  onCancel: () => void;
  isLoading: boolean;
  isEstimationLoading: boolean;
  estimatedCost: number;
  availableCredits: number;
  calculateEstimation: (prompt: string, language: string) => void;
  onGenerateFromPrompt: (model: LessonGenerationFromPromptModel) => void;
  onOpenLibrary: (
    fileType: 'video' | 'document',
    selectedFile?: MediaFile,
  ) => Promise<MediaFile>;
  onGenerateFromVideo: (
    includeBranch: boolean,
    includeGrading: boolean,
    contentId: string,
    language: string,
    dialect: string,
    gender: Gender,
    name: string,
    passingScore?: number,
    addAckNode?: boolean,
    mediaType?: 'video' | 'document',
    generateMicroreels?: boolean,
  ) => void;
  isGenerationAvailable: boolean;
  llmProvider: string;
}

const GenerateMicrolessonUI = React.memo(
  ({
    onCancel,
    isLoading,
    isEstimationLoading,
    estimatedCost,
    availableCredits,
    calculateEstimation,
    onGenerateFromPrompt,
    onOpenLibrary,
    onGenerateFromVideo,
    isGenerationAvailable,
    llmProvider,
  }: Props) => {
    const [prompt, setPrompt] = useState('');
    const [includeBranching, setIncludeBranching] = useState('1');
    const [includeGrading, setIncludeGrading] = useState('1');
    const [passingScore, setPassingScore] = useState<number | undefined>(50);
    const [enableAckNode, setEnableAckNode] = useState('1');
    const [ackNodePreference, setAckNodePreference] = useState('1');
    const [source, setSource] = useState('1');
    const [videoFile, setVideoFile] = useState<MediaFile>();
    const [language, setLanguage] = useState<LanguageKey>('en');
    const [dialectOptions, setDialectOptions] = useState<SelectOption[]>([]);
    const [selectedDialect, setSelectedDialect] = useState<DialectKey>('');
    const [voiceGender, setVoiceGender] = useState<Gender>('female');
    const [title, setTitle] = useState('');
    const [generateMicroreels, setGenerateMicroreels] = useState(true);

    const [lngDropdownIsOpen, setLngDropdownIsOpen] = useState(false);
    const [dialectDropdownIsOpen, setDialectDropdownIsOpen] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
      calculateEstimation(prompt, language);
    }, [prompt, language]);

    useEffect(() => {
      const config = LANGUAGE_CONFIG[language];
      if (!config) return;
      const dialects = Object.entries(config.dialects);
      const dialectOptions: SelectOption[] = dialects.map(([dialectKey]) => ({
        key: dialectKey,
        value: dialectKey,
      }));
      setDialectOptions(dialectOptions);
      if (dialects.length > 0) {
        const defaultDialect = dialectOptions[0].key;
        setSelectedDialect(defaultDialect);
        const defaultGender = config.dialects[defaultDialect];
        setVoiceGender(defaultGender);
      } else {
        setSelectedDialect('');
        setVoiceGender('female');
      }
    }, [language]);

    useEffect(() => {
      setPrompt('');
      setVideoFile(undefined);
    }, [source]);

    useEffect(() => {
      if (includeBranching === '0') {
        setGenerateMicroreels(false);
      }
    }, [includeBranching]);

    useEffect(() => {
      if (includeGrading === '1') {
        setEnableAckNode('0');
        return;
      }
      setEnableAckNode(ackNodePreference);
    }, [includeGrading, ackNodePreference]);

    const handleAckNodeChange = (selectedKey: string) => {
      setAckNodePreference(selectedKey);
      setEnableAckNode(selectedKey);
    };

    const saveEnabled = () => {
      if (!title) return false;

      if (source === '1') {
        return (
          prompt !== '' &&
          selectedDialect !== '' &&
          availableCredits > estimatedCost
        );
      }

      if (source === '2') {
        return videoFile !== undefined;
      }

      return false;
    };

    const showLibrary = async () => {
      const file = await onOpenLibrary('video');
      if (!file) return;
      setVideoFile(file);
    };

    const handleSave = () => {
      if (!isGenerationAvailable) {
        setErrors([internalErrorCodes.AI_GENERATION_NOT_AVAILABLE]);
        return;
      }
      if (source === '1') {
        onGenerateFromPrompt({
          prompt,
          addBranching: includeBranching === '1',
          addAssessment: includeGrading === '1',
          passingScore,
          addAckNode: enableAckNode === '1',
          generateMicroreels,
          language: language,
          dialect: selectedDialect,
          gender: voiceGender,
          name: title,
        });
      } else if (source === '2') {
        onGenerateFromVideo(
          includeBranching === '1',
          includeGrading === '1',
          videoFile?.storageFileName || '',
          language,
          selectedDialect,
          voiceGender,
          title,
          passingScore,
          enableAckNode === '1',
          'video',
          generateMicroreels,
        );
      }
    };

    return (
      <div className="GenerateMicrolessonsContainer">
        {source === '1' && <Tour section="microlessons" />}
        <PageTitle
          title={generateMicrolessonsText.title}
          subtitle={generateMicrolessonsText.subtitle}
        />
        <div className="TitleContainer">
          <BackButton />
          <h2>{generateMicrolessonsText.generateTitle}</h2>
          <div />
        </div>
        <div className="GenerateMicrolessonBody">
          <div className="GenerateMicrolessonCard">
            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.source}
              <span className="MandatoryIndicator">*</span>
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.sourceSubtitle}
            </p>
            <ToggleSwitch
              options={SOURCE_TYPE}
              selectedKey={source}
              onChange={setSource}
            />
            {source === '1' && (
              <>
                <p className="LessonGenVideoTitle">
                  {generateMicrolessonsText.promptTitle}
                </p>
                <span className="PromptInfo">
                  {generateMicrolessonsText.promptSubtitle}
                </span>
                <TextAreaInput
                  placeholder={generateMicrolessonsText.promptPlaceholder}
                  value={prompt}
                  onChange={setPrompt}
                  maxCharacters={250}
                />
              </>
            )}
            {source === '2' && (
              <>
                <p className="LessonGenVideoTitle">
                  {generateMicrolessonsText.videoTitle}
                </p>
                <LibraryFilesInput
                  onBrowse={showLibrary}
                  files={videoFile ? [videoFile] : []}
                  onRemoveFile={() => setVideoFile(undefined)}
                  singleFile
                />
              </>
            )}

            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.titleField}
              <span className="MandatoryIndicator">*</span>
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.titleDescription}
            </p>
            <TextInput
              value={title}
              placeholder={generateMicrolessonsText.titleField}
              onChange={setTitle}
              maxCharacters={40}
            />

            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.includeBranching}
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.branchingSubtitle}
            </p>
            <ToggleSwitch
              options={BRANCHING_OPTIONS}
              selectedKey={includeBranching}
              onChange={setIncludeBranching}
            />
            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.includeGrading}
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.assesmentSubtitle}
            </p>
            <ToggleSwitch
              options={ASSESMENT_OPTIONS}
              selectedKey={includeGrading}
              onChange={setIncludeGrading}
            />
            {includeGrading === '1' && (
              <>
                <p className="LessonGenVideoTitle">
                  {generateMicrolessonsText.passingScore}
                </p>
                <p className="LessonGenVideoSubTitle">
                  {generateMicrolessonsText.passingScoreSubtitle}
                </p>
                <NumbersInput
                  value={passingScore}
                  setValue={setPassingScore}
                  disabled={!includeGrading}
                  className="LessonGenPassingScore"
                />
              </>
            )}
            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.enableAckNode}
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.enableAckNodeSubtitle}
            </p>
            <ToggleSwitch
              options={ACK_NODE_OPTIONS}
              selectedKey={enableAckNode}
              onChange={handleAckNodeChange}
              disabled={includeGrading === '1'}
            />
            <p className="LessonGenVideoTitle">
              {generateMicrolessonsText.selectLanguage}
            </p>
            <p className="LessonGenVideoSubTitle">
              {generateMicrolessonsText.selectLanguageSubtitle}
            </p>
            <div className="DropdownGroup">
              <Dropdown
                options={LANGUAGE_OPTIONS}
                selectedOption={language}
                setSelectedOption={lang => setLanguage(lang as LanguageKey)}
                className="LanguageDropdown"
                onDropdownVisibilityToggle={setLngDropdownIsOpen}
              />
              <Dropdown
                options={dialectOptions}
                selectedOption={selectedDialect}
                setSelectedOption={setSelectedDialect}
                className="LanguageDropdown"
                onDropdownVisibilityToggle={setDialectDropdownIsOpen}
              />
            </div>
            <h5 className="LessonGenVideoTitle">
              {generateMicrolessonsText.generateMicroreels}
            </h5>
            <YesNoOption
              onSelect={setGenerateMicroreels}
              value={generateMicroreels}
              className="OptionsSection"
              disabled={includeBranching === '0'}
            />
            {(source === '1' || source === '2') && (
              <div className="Estimation">
                <ul>
                  <li>
                    {generateAIVideosText.summaryAvailableCredits}
                    <span
                      className={`CreditsCost ${
                        availableCredits <
                        (source === '1'
                          ? estimatedCost
                          : llmProvider === 'google'
                          ? 500
                          : 4000)
                          ? 'CreditError'
                          : ''
                      }`}>
                      {numberWithCommas(availableCredits.toString())}
                    </span>
                  </li>
                  <li>
                    {generateAIVideosText.summaryEstimatedCost}
                    <span className="CreditsCost">
                      {source === '1'
                        ? isEstimationLoading
                          ? generateAIVideosText.calculating
                          : numberWithCommas(estimatedCost.toString())
                        : numberWithCommas(
                            llmProvider === 'google' ? '500' : '4000',
                          )}
                    </span>
                  </li>
                </ul>
              </div>
            )}
            <ErrorBox2
              errorCodes={errors}
              clearErrors={() => setErrors([])}
              className="GenerateMicrolessonErrorBox"
              uiType="standard"
            />
            <div className="Footer">
              <Button
                title={generateMicrolessonsText.cancel}
                onClick={onCancel}
                uiType="text"
              />
              <Button
                title={generateMicrolessonsText.generate}
                onClick={handleSave}
                uiType="action"
                isLoading={isLoading}
                disabled={!saveEnabled()}
              />
            </div>
            {(lngDropdownIsOpen || dialectDropdownIsOpen) && (
              <div className="Spacer" />
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default GenerateMicrolessonUI;
